<template>
  <div>
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          {{ $t('Loading') }}...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-form ref="form" @submit.prevent="search" autocomplete="off">
      <slot :search="search">
        <v-container class="elevation-0 py-0">
          <v-row>
            <v-col>
              <v-text-field
                prefix="Search"
                :placeholder="communityAbbreviation ? $t('Across_This_Community') : $t('Across_All_Communities')"
                v-model="query"
                variant="solo"
                solo
                dense
                density="comfortable"
                hide-details
                max-length="60"
                class="txt-search"
              >
                <template v-slot:append-inner>
                  <v-btn small color="primary" @click="search" class="search-btn-default">
                    {{ $t('Search') }}
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </slot>
      <v-container class="elevation-0 py-0">
        <v-row dense>
          <v-col class="py-0">
            <v-alert type="warning" dense text v-if="validationMessage">
              {{ validationMessage }}
            </v-alert>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <v-dialog v-model="showResults" fullscreen transition="dialog-bottom-transition" class="content-dialog">
      <v-card class="result-content" ref="dialogContent">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="onClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-row dense class="align-center">
              <v-col>
                {{ $t('Total') }}: {{ total }}. {{ $t('Page') }}: {{ page }} / {{ totalPages }}. {{ $t('Search') }}:
                <em>{{ searchTerm }}</em
                >. <span v-if="communityAbbreviation">{{ $t('Community') }}: {{ communityAbbreviation }}</span>
              </v-col>
              <v-col class="text-end">
                <CsvExport
                  :data="exportData"
                  :hasBeforeGenerateEvent="true"
                  @beforeGenerate="downloadCsv($event)"
                  :fileName="`${$t('Search')}: ${searchTerm}.csv`"
                />
              </v-col>
            </v-row>
          </v-toolbar-title>
        </v-toolbar>
        <v-container>
          <v-row dense>
            <v-col cols="12" v-for="result in results" :key="result.id">
              <p>
                <ArchiveButton
                  :ref="`archiveButton-${result.id}`"
                  :mocId="result.id"
                  v-if="isClosedStatus(result.status)"
                  :isEmoc1Archive="isEmoc1Id(result.id)"
                />
                <a v-if="!isEmoc1Id(result.id)" class="link" :href="getMocUrl(result.id)"
                  ><strong>{{ result.id }}</strong> - {{ result.community }} - {{ result.title || '' }}</a
                >

                <!-- Emoc1 -->
                <a v-else class="link" href="#" @click.prevent="downloadEmoc1(result.id)"
                  ><strong>{{ result.id }}</strong> - {{ result.community }} - {{ result.title || '' }}</a
                >
              </p>
              <p>{{ $t('Status') }}: {{ result.status }}</p>
              <p>
                <span>{{ result.description }}</span>
              </p>
            </v-col>
          </v-row>
        </v-container>
        <v-container fluid>
          <v-row dense class="navigation-links">
            <v-col cols="6" class="content-start px-4">
              <a href="#" v-if="page > 1" class="blue-link" @click.prevent="goBack"
                ><v-icon class="navigation-icon">mdi-chevron-left</v-icon> {{ $t('Previous') }}</a
              >
            </v-col>
            <v-col cols="6" class="content-end px-4">
              <a href="#" v-if="page * limit < total" class="blue-link" @click.prevent="next"
                >{{ $t('Next') }} <v-icon class="navigation-icon">mdi-chevron-right</v-icon></a
              >
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <toaster ref="toaster"></toaster>
  </div>
</template>
<script>
import MOCService from '@/services/moc.service'
import Toaster from '@/components/Toaster'
import CsvExport from '@/components/CsvExport'
import ArchiveButton from '@/components/archive/ArchiveButton'
import { FormStatus } from '@/common'

export default {
  name: 'SearchMoc',
  components: {
    Toaster,
    CsvExport,
    ArchiveButton
  },
  props: {
    searchFor: String,
    communityAbbreviation: String
  },
  data() {
    return {
      results: [],
      highlights: {},
      scores: {},
      total: 0,
      query: '',
      page: 1,
      limit: 10,
      loading: false,
      showResults: false,
      validationMessage: '',
      exportData: []
    }
  },
  computed: {
    searchTerm() {
      return this.searchFor ? this.searchFor : this.query
    },
    totalPages() {
      return Math.ceil(this.total / this.limit)
    }
  },
  methods: {
    isClosedStatus(status) {
      const statusName = status.toLowerCase()
      switch (statusName) {
        case FormStatus.properties[FormStatus.CLOSED].name.toLowerCase():
        case FormStatus.properties[FormStatus.CLOSEDREJECTED].name.toLowerCase():
        case FormStatus.properties[FormStatus.CLOSEDVOIDED].name.toLowerCase():
        case FormStatus.properties[FormStatus.CLOSEDVETOED].name.toLowerCase():
        case 'closed stranded':
          return true
        default:
          return false
      }
    },
    downloadCsv(done) {
      this.loading = true
      MOCService.search(this.searchTerm, 1, 5000, this.communityAbbreviation)
        .then((response) => {
          this.exportData = response.results.map((r) => {
            return {
              [this.$t('MOC_ID')]: r.id,
              URL: `${window.location.protocol}//${window.location.hostname}/${r.communityAbbreviation}/moc/${r.id}`,
              [this.$t('Community')]: r.community,
              [this.$t('Status')]: r.status,
              [this.$t('Title')]: r.title,
              [this.$t('MOC_Description')]: r.description,
              [this.$t('Change_Category')]: r.changeCategory,
              [this.$t('MOC_Type')]: r.mocType
            }
          })
          this.$nextTick(() => done())
        })
        .catch((e) => {
          this.$refs.toaster.pop('error', this.$t('Error_Search'))
        })
        .finally(() => (this.loading = false))
    },
    search(cb) {
      this.validationMessage = this.searchTerm && this.searchTerm.trim() ? false : this.$t('Prompt_TypeToSearch')
      if (this.validationMessage) return
      this.validationMessage = ''
      this.loading = true
      MOCService.search(this.searchTerm, this.page, this.limit, this.communityAbbreviation)
        .then((response) => {
          if (!response.results.length) {
            this.$refs.toaster.pop('info', this.$t('No_Results'))
            this.query = ''
            this.$emit('onClose')
            return
          }
          if (typeof cb === 'function') cb()
          this.showResults = true
          this.results = response.results
          this.highlights = response.highlights
          this.total = response.total
          this.scores = response.scores
          this.exportData = response.results
        })
        .catch((e) => {
          this.$refs.toaster.pop('error', this.$t('Error_Search'))
        })
        .finally(() => (this.loading = false))
    },
    getScores(id) {
      return Math.round(this.scores[id])
    },
    getMocUrl(id) {
      return `/${id.substring(0, id.indexOf('-'))}/moc/${id}`
    },
    goBack() {
      this.page--
      this.search(() => {
        this.$nextTick(() => {
          const content = this.$refs.dialogContent
          if (content) {
            content.scrollTop = 0
          }
        })
      })
    },
    next() {
      this.page++
      this.search(() => {
        this.$nextTick(() => {
          const content = this.$refs.dialogContent
          if (content) {
            content.scrollTop = 0
          }
        })
      })
    },
    onClose() {
      this.showResults = false
      this.query = ''
      this.$emit('onClose')
    },
    isEmoc1Id(mocId) {
      return mocId.toLowerCase().startsWith('emoc-')
    },
    downloadEmoc1(mocId) {
      this.$refs[`archiveButton-${mocId}`][0].downloadEmoc1Archive()
    }
  },
  watch: {
    showResults() {
      this.page = 1
    },
    searchTerm(newValue) {
      if (newValue && newValue.trim()) this.validationMessage = ''
    }
  }
}
</script>
<style scoped>
.result-content a {
  color: #0000ee !important;
}

.navigation-links a {
  font-size: 16px;
  text-decoration: none;
}

.navigation-icon {
  top: -1px;
  position: relative;
  color: #0000ee;
}

.bordered {
  border: 1px solid black;
}

.content-end {
  justify-content: flex-end;
  display: flex;
}

.content-start {
  justify-content: flex-start;
  display: flex;
}

.search-error {
  font-size: 12px;
}

.search-btn-default {
  position: relative;
}
</style>

<style>
.v-text-field__prefix {
  opacity: 1 !important;
}
.txt-search input.v-field__input {
  padding-left: 5px;
}
</style>
